import React, { useState, useEffect } from 'react';
import { X, FileText, Calendar, Hash, Percent, Package, MapPin, ArrowRight, Building2, Route, Loader2 } from 'lucide-react';
import RouteModal from './RouteModal';
import ConstructionSiteModal from './ConstructionSiteModal';
import ProductionLocationModal from './ProductionLocationModal';
import { simulationService } from '../../services/simulationService';

// Google Maps API configuration
const GOOGLE_MAPS_API_KEY = 'AIzaSyBSTQdQL17yVci86qzot_IPHKuJGZ8FQiA';

// Distance calculation utility function
const calculateDistance = async (origin, destination) => {
  if (!origin || !destination) {
    return null;
  }

  try {
    // Check if Google Maps API is available
    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps API not loaded');
    }

    const service = new window.google.maps.DistanceMatrixService();

    return new Promise((resolve, reject) => {
      service.getDistanceMatrix({
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
        avoidHighways: false,
        avoidTolls: false
      }, (response, status) => {
        if (status === window.google.maps.DistanceMatrixStatus.OK) {
          const element = response.rows[0].elements[0];
          if (element.status === 'OK') {
            // Convert meters to kilometers and round to 1 decimal place
            const distanceKm = Math.round(element.distance.value / 100) / 10;
            resolve(distanceKm);
          } else {
            resolve(null);
          }
        } else {
          reject(new Error(`Distance Matrix API error: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('Distance calculation error:', error);
    return null;
  }
};

// Load Google Maps API
const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      existingScript.addEventListener('load', resolve);
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps API')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Double-check that the API is actually loaded
      if (window.google && window.google.maps) {
        resolve();
      } else {
        reject(new Error('Google Maps API failed to initialize'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    document.head.appendChild(script);
  });
};

const MaterialSpecificationModal = ({ isOpen, onClose, data, isLoading, error, onTransferValues, initialConstructionSite, materialName, projectName, onMaterialDichteApplied }) => {
  const [isApplyingDichte, setIsApplyingDichte] = useState(false);
  const [dichteApplyError, setDichteApplyError] = useState(null);
  // Distance calculation state
  const [distances, setDistances] = useState({});
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  // Route data state - stores complete route information including waypoints
  const [routeData, setRouteData] = useState({});

  // Route modal state
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState({ origin: null, destination: null, originLocation: null, destinationLocation: null, originalCacheKey: null });

  // Normalize an incoming location prop to the shape ConstructionSiteModal expects
  // (it reads `.coordinates` for the map center).
  const normalizeConstructionSite = (loc) => {
    if (!loc?.address) return null;
    const lat = loc.lat ?? loc.coordinates?.lat;
    const lng = loc.lng ?? loc.coordinates?.lng;
    if (lat === undefined || lng === undefined) return null;
    return {
      address: loc.address,
      lat,
      lng,
      coordinates: { lat, lng }
    };
  };

  // Construction site modal state
  const [constructionSiteModalOpen, setConstructionSiteModalOpen] = useState(false);
  const [constructionSite, setConstructionSite] = useState(
    normalizeConstructionSite(initialConstructionSite)
  );

  // Production plant (Lieferwerk) location: frontend-geocoded, user-editable
  const [productionPlantLocation, setProductionPlantLocation] = useState(null);
  const [productionPlantModalOpen, setProductionPlantModalOpen] = useState(false);
  const [plantGeocodeLoading, setPlantGeocodeLoading] = useState(false);

  // Single Hersteller (manufacturer) entry for the binder section: name + optional location.
  // Local-only state — not transferred or persisted yet.
  const [binderHersteller, setBinderHersteller] = useState({});
  const [binderHerstellerModalOpen, setBinderHerstellerModalOpen] = useState(false);

  // Sync construction site when the project location prop changes (e.g. switching projects)
  useEffect(() => {
    const normalized = normalizeConstructionSite(initialConstructionSite);
    if (normalized) {
      setConstructionSite(normalized);
    }
  }, [initialConstructionSite]);

  // Load Google Maps API when modal opens
  useEffect(() => {
    if (isOpen && !googleMapsLoaded) {
      loadGoogleMapsAPI()
        .then(() => {
          setGoogleMapsLoaded(true);
        })
        .catch((error) => {
          console.error('Failed to load Google Maps API:', error);
        });
    }
  }, [isOpen, googleMapsLoaded]);

  // Geocode the Lieferwerk to the actual plant location.
  // PDFs typically print the company HQ address rather than the plant's, so we
  // ignore data.material_info.productionAddress and resolve via Google with an
  // "Asphaltmischwerk <name>" prefix when the name looks like a short city/site
  // (skipped if it already reads like a full company name).
  useEffect(() => {
    if (!googleMapsLoaded) return;
    const name = data?.material_info?.productionLocation;
    if (!name) return;
    if (productionPlantLocation?.queriedFor === name) return;

    const looksLikeFullCompany = /asphalt|mischwerk|gmbh|\bkg\b/i.test(name);
    const queries = looksLikeFullCompany
      ? [name, `${name}, Germany`]
      : [
          `Asphaltmischwerk ${name}`,
          `Asphaltmischwerk ${name}, Germany`,
          `${name} Asphaltmischwerk`,
          name,
          `${name}, Germany`,
        ];

    const geocoder = new window.google.maps.Geocoder();
    let cancelled = false;
    setPlantGeocodeLoading(true);

    const tryNext = (i) => {
      if (cancelled) return;
      if (i >= queries.length) {
        setPlantGeocodeLoading(false);
        return;
      }
      geocoder.geocode({ address: queries[i], region: 'de' }, (results, status) => {
        if (cancelled) return;
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          setProductionPlantLocation({
            address: results[0].formatted_address,
            lat,
            lng,
            coordinates: { lat, lng },
            queriedFor: name,
            queryUsed: queries[i],
          });
          setPlantGeocodeLoading(false);
        } else {
          tryNext(i + 1);
        }
      });
    };
    tryNext(0);

    return () => {
      cancelled = true;
    };
  }, [googleMapsLoaded, data?.material_info?.productionLocation]);

  // Calculate distances when data is available and Google Maps is loaded.
  // Source of truth for the destination is the geocoded Lieferwerk coordinate
  // (productionPlantLocation) when available; otherwise the raw productionLocation
  // string as a fallback while geocoding is still in flight or has failed.
  useEffect(() => {
    const calculateDistances = async () => {
      if (!data?.full_data?.aggregateComposition ||
          !data?.material_info?.productionLocation ||
          !googleMapsLoaded) {
        return;
      }
      // No distanceLoading early-return: the cache check below dedupes work,
      // and the early-return would race when productionPlantLocation flips
      // mid-flight — the second run would bail and never recompute coord keys.

      const destinationApi = productionPlantLocation
        ? { lat: productionPlantLocation.lat, lng: productionPlantLocation.lng }
        : data.material_info.productionLocation;
      const destinationKey = productionPlantLocation
        ? `${productionPlantLocation.lat},${productionPlantLocation.lng}`
        : data.material_info.productionLocation;

      // Check aggregate composition distances
      const needsAggregateCalculation = data.full_data.aggregateComposition.some(item => {
        const origin = item.bezugsquelle || item.gewinnungsstatte;
        if (origin && origin !== 'Nicht verfügbar') {
          const cacheKey = `${origin}-${destinationKey}`;
          return distances[cacheKey] === undefined;
        }
        return false;
      });

      // Check construction site distance
      const needsConstructionSiteCalculation = constructionSite?.address &&
        distances[`${constructionSite.address}-${destinationKey}`] === undefined;

      // Check binder Hersteller distance (Hersteller → Lieferwerk)
      const herstellerHasCoords = typeof binderHersteller?.lat === 'number' && typeof binderHersteller?.lng === 'number';
      const herstellerKey = herstellerHasCoords
        ? `${binderHersteller.lat},${binderHersteller.lng}`
        : null;
      const needsHerstellerCalculation = herstellerHasCoords &&
        distances[`${herstellerKey}-${destinationKey}`] === undefined;

      const needsCalculation = needsAggregateCalculation || needsConstructionSiteCalculation || needsHerstellerCalculation;

      if (!needsCalculation) {
        return;
      }

      setDistanceLoading(true);
      const newDistances = {};

      try {
        // Calculate distances for each aggregate source
        for (const item of data.full_data.aggregateComposition) {
          const origin = item.bezugsquelle || item.gewinnungsstatte;
          if (origin && origin !== 'Nicht verfügbar') {
            const cacheKey = `${origin}-${destinationKey}`;

            if (distances[cacheKey] === undefined) {
              try {
                const distance = await calculateDistance(origin, destinationApi);
                newDistances[cacheKey] = distance;

                // Throttle to avoid Distance Matrix rate limits
                await new Promise(resolve => setTimeout(resolve, 200));
              } catch (error) {
                console.error(`Failed to calculate distance for ${origin}:`, error);
                newDistances[cacheKey] = null;
              }
            }
          }
        }

        // Calculate construction site distance if needed
        if (constructionSite?.address) {
          const cacheKey = `${constructionSite.address}-${destinationKey}`;
          if (distances[cacheKey] === undefined) {
            try {
              const distance = await calculateDistance(constructionSite.address, destinationApi);
              newDistances[cacheKey] = distance;
            } catch (error) {
              console.error(`Failed to calculate construction site distance:`, error);
              newDistances[cacheKey] = null;
            }
          }
        }

        // Calculate binder Hersteller distance if set
        if (herstellerHasCoords) {
          const cacheKey = `${herstellerKey}-${destinationKey}`;
          if (distances[cacheKey] === undefined) {
            try {
              const distance = await calculateDistance(
                { lat: binderHersteller.lat, lng: binderHersteller.lng },
                destinationApi,
              );
              newDistances[cacheKey] = distance;
            } catch (error) {
              console.error(`Failed to calculate Hersteller distance:`, error);
              newDistances[cacheKey] = null;
            }
          }
        }

        if (Object.keys(newDistances).length > 0) {
          setDistances(prev => ({ ...prev, ...newDistances }));
        }
      } catch (error) {
        console.error('Error calculating distances:', error);
      } finally {
        setDistanceLoading(false);
      }
    };

    calculateDistances();
  }, [data, googleMapsLoaded, constructionSite, productionPlantLocation, binderHersteller]);

  if (!isOpen) return null;

  // Cache key for the current Lieferwerk destination — coords if geocoded,
  // otherwise the raw productionLocation string. Must match the keys written
  // by the calculateDistances effect above.
  const destinationCacheKey = productionPlantLocation
    ? `${productionPlantLocation.lat},${productionPlantLocation.lng}`
    : data?.material_info?.productionLocation;

  // Helper function to get distance for a specific location
  const getDistance = (origin) => {
    if (!origin || origin === 'Nicht verfügbar' || !destinationCacheKey) {
      return null;
    }
    return distances[`${origin}-${destinationCacheKey}`];
  };

  // Helper function to get construction site to production location distance
  const getConstructionSiteDistance = () => {
    if (!constructionSite?.address || !destinationCacheKey) {
      return null;
    }
    return distances[`${constructionSite.address}-${destinationCacheKey}`];
  };

  // Distance from binder Hersteller → Lieferwerk (uses coord cache key)
  const getBinderHerstellerDistance = () => {
    if (typeof binderHersteller?.lat !== 'number' || typeof binderHersteller?.lng !== 'number' || !destinationCacheKey) {
      return null;
    }
    const herstellerKey = `${binderHersteller.lat},${binderHersteller.lng}`;
    return distances[`${herstellerKey}-${destinationCacheKey}`];
  };

  // Helper function to format distance display
  const formatDistance = (distance) => {
    if (distance === null || distance === undefined) {
      return distanceLoading ? (
        <div className="flex items-center">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          <span className="text-xs text-gray-500">Berechnung...</span>
        </div>
      ) : (
        <span className="text-gray-400">-</span>
      );
    }
    return `${distance} km`;
  };

  // Route modal handlers — destination always derives from the Lieferwerk:
  // the readable label comes from materialInfo.productionLocation, but the
  // routed point comes from productionPlantLocation when geocoded so that
  // RouteModal renders the correct plant rather than the city centroid.
  const handleRouteClick = (origin) => {
    const destinationLabel = data?.material_info?.productionLocation;
    if (!origin || origin === 'Nicht verfügbar' || !destinationLabel) {
      return;
    }
    const destinationLocation = productionPlantLocation
      ? { lat: productionPlantLocation.lat, lng: productionPlantLocation.lng }
      : null;
    const destinationKeyPart = destinationLocation
      ? `${destinationLocation.lat},${destinationLocation.lng}`
      : destinationLabel;
    const originalCacheKey = `${origin}-${destinationKeyPart}`;
    setSelectedRoute({
      origin,
      destination: destinationLabel,
      originLocation: null,
      destinationLocation,
      originalCacheKey,
    });
    setRouteModalOpen(true);
  };

  // Bindemittel row: routes binder Hersteller → Lieferwerk.
  const handleBindemittelRouteClick = () => {
    const destinationLabel = data?.material_info?.productionLocation;
    if (typeof binderHersteller?.lat !== 'number' || typeof binderHersteller?.lng !== 'number' || !destinationLabel) {
      return;
    }
    const originLabel = binderHersteller.name || binderHersteller.address || 'Hersteller';
    const originLoc = { lat: binderHersteller.lat, lng: binderHersteller.lng };
    const destinationLoc = productionPlantLocation
      ? { lat: productionPlantLocation.lat, lng: productionPlantLocation.lng }
      : null;
    const herstellerKey = `${binderHersteller.lat},${binderHersteller.lng}`;
    const originalCacheKey = `${herstellerKey}-${destinationCacheKey}`;
    setSelectedRoute({
      origin: originLabel,
      destination: destinationLabel,
      originLocation: originLoc,
      destinationLocation: destinationLoc,
      originalCacheKey,
    });
    setRouteModalOpen(true);
  };

  // Asphaltmischgut row: routes Lieferwerk → Baustelle. Shares the cache key
  // with the Baustelle↔Lieferwerk pair (DistanceMatrix is symmetric for driving),
  // so the displayed km matches the existing Asphaltgranulat row.
  const handleAsphaltmischgutRouteClick = () => {
    const originLabel = data?.material_info?.productionLocation;
    const destinationLabel = constructionSite?.address;
    if (!originLabel || !destinationLabel) {
      return;
    }
    const originLoc = productionPlantLocation
      ? { lat: productionPlantLocation.lat, lng: productionPlantLocation.lng }
      : null;
    const destinationLoc = constructionSite
      ? { lat: constructionSite.lat, lng: constructionSite.lng }
      : null;
    const originalCacheKey = `${destinationLabel}-${destinationCacheKey}`;
    setSelectedRoute({
      origin: originLabel,
      destination: destinationLabel,
      originLocation: originLoc,
      destinationLocation: destinationLoc,
      originalCacheKey,
    });
    setRouteModalOpen(true);
  };

  const handleRouteModalClose = () => {
    setRouteModalOpen(false);
    setSelectedRoute({ origin: null, destination: null, originLocation: null, destinationLocation: null, originalCacheKey: null });
  };

  // Handle saving updated distance from RouteModal
  const handleRouteSave = (savedRouteData) => {
    if (!savedRouteData || savedRouteData.distance === null || !selectedRoute.originalCacheKey) {
      console.warn('handleRouteSave: Invalid route data or missing original cache key');
      return;
    }

    console.log('Updating route data:', {
      route: selectedRoute.originalCacheKey,
      newDistance: savedRouteData.distance,
      waypoints: savedRouteData.waypoints
    });

    // Update the distances state using the original cache key
    // This ensures the UI shows the updated distance for the correct aggregate entry
    setDistances(prev => ({
      ...prev,
      [selectedRoute.originalCacheKey]: savedRouteData.distance
    }));

    // Store the complete route data including waypoints
    setRouteData(prev => ({
      ...prev,
      [selectedRoute.originalCacheKey]: savedRouteData
    }));

    // Close the route modal
    handleRouteModalClose();
  };

  // Construction site modal handlers
  const handleConstructionSiteClick = () => {
    setConstructionSiteModalOpen(true);
  };

  const handleConstructionSiteModalClose = () => {
    setConstructionSiteModalOpen(false);
  };

  const handleConstructionSiteSave = (siteData) => {
    setConstructionSite(siteData);
    setConstructionSiteModalOpen(false);
    console.log('Construction site saved:', siteData);
  };

  // Production plant (Lieferwerk) modal handlers
  const handleProductionPlantClick = () => {
    setProductionPlantModalOpen(true);
  };

  const handleProductionPlantModalClose = () => {
    setProductionPlantModalOpen(false);
  };

  const handleProductionPlantSave = (locationData) => {
    setProductionPlantLocation(prev => ({
      ...locationData,
      queriedFor: prev?.queriedFor ?? data?.material_info?.productionLocation,
    }));
    setProductionPlantModalOpen(false);
  };

  // Binder Hersteller handlers (single shared entry under the binder cards)
  const handleBinderHerstellerNameChange = (name) => {
    setBinderHersteller(prev => ({ ...prev, name }));
  };

  const handleBinderHerstellerClick = () => {
    setBinderHerstellerModalOpen(true);
  };

  const handleBinderHerstellerModalClose = () => {
    setBinderHerstellerModalOpen(false);
  };

  const handleBinderHerstellerSave = (locationData) => {
    setBinderHersteller(prev => ({ ...prev, ...locationData }));
    setBinderHerstellerModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht verfügbar';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE');
    } catch {
      return dateString;
    }
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null) return 'Nicht verfügbar';
    return `${value}%`;
  };

  // Available bindemittel options from BindemittelSelect component
  const bindemittelOptions = [
    "30/45", "50/70", "70/100", "160/220", "10/40-65 A", "40/100-65 A",
    "25/55-55 A", "45/80-50 A", "15/25 VL", "15/25 VH", "25/35 VL",
    "25/35 VH", "35/50 VL", "35/50 VH", "50/80 VL", "50/80 VH",
    "PmB 10/25 VL", "PmB 10/25 VH", "PmB 25/45 VL", "PmB 25/45 VH",
    "PmB 50/80 VL", "PmB 50/80 VH"
  ];

  const mapBinderType = (extractedType) => {
    if (!extractedType) return null;

    const typeStr = extractedType.toLowerCase().trim();

    // Direct mapping for common patterns
    const mappings = {
      'bitumen 30/45': '30/45',
      'bitumen 50/70': '50/70',
      'bitumen 70/100': '70/100',
      'bitumen 160/220': '160/220',
      '30/45': '30/45',
      '50/70': '50/70',
      '70/100': '70/100',
      '160/220': '160/220'
    };

    // Check direct mappings first
    if (mappings[typeStr]) {
      return mappings[typeStr];
    }

    // Extract pattern like "30/45" from strings like "Bitumen 30/45" or "PmB 30/45"
    const numberPattern = /(\d+\/\d+)/;
    const match = extractedType.match(numberPattern);

    if (match) {
      const extractedNumber = match[1];
      // Check if this number pattern exists in our options
      const foundOption = bindemittelOptions.find(option =>
        option.includes(extractedNumber)
      );
      if (foundOption) {
        return foundOption;
      }
    }

    // Check for PmB patterns
    if (typeStr.includes('pmb') || typeStr.includes('polymer')) {
      const pmbMatch = extractedType.match(/(\d+\/\d+)/);
      if (pmbMatch) {
        const numberPart = pmbMatch[1];
        // Try to find PmB variant
        const pmbOption = bindemittelOptions.find(option =>
          option.startsWith('PmB') && option.includes(numberPart)
        );
        if (pmbOption) {
          return pmbOption;
        }
      }
    }

    return null; // Return null if no mapping found
  };

  const extractAllValues = () => {
    const result = {
      fueller: null,
      gesteinComponents: [], // Individual gestein components instead of single aggregated value
      asphalt: null,
      bindemittel: null,
      selectedBindemittel: null,
      // Distance values
      fuellerDistance: null,
      asphaltDistance: null,
      bindemittelDistance: null,
    };

    // 1. Extract Füller M.-% from Aggregate Composition
    if (data?.full_data?.aggregateComposition) {
      const fuellerRow = data.full_data.aggregateComposition.find(item => {
        const lieferkornung = item.lieferkornung?.toLowerCase() || '';
        const bezeichnung = item.bezeichnung?.toLowerCase() || '';
        return lieferkornung.includes('füller') || bezeichnung.includes('füller');
      });

      if (fuellerRow?.massPercentage !== undefined && fuellerRow.massPercentage !== null) {
        result.fueller = Math.round(fuellerRow.massPercentage * 10) / 10;
      }

      // 2. Extract individual Gesteinskörnungen components (non-Füller rows)
      const nonFuellerRows = data.full_data.aggregateComposition.filter(item => {
        const lieferkornung = item.lieferkornung?.toLowerCase() || '';
        const bezeichnung = item.bezeichnung?.toLowerCase() || '';
        return !(lieferkornung.includes('füller') || bezeichnung.includes('füller'));
      });

      nonFuellerRows.forEach(item => {
        if (item.massPercentage !== undefined && item.massPercentage !== null) {
          const source = item.bezugsquelle || item.gewinnungsstatte;
          const distance = (source && source !== 'Nicht verfügbar') ? getDistance(source) : null;
          result.gesteinComponents.push({
            nr: item.nr || 'N/A',
            bezeichnung: item.bezeichnung || 'N/A',
            lieferkornung: item.lieferkornung || 'N/A',
            percentage: Math.round(item.massPercentage * 10) / 10,
            distance: distance,
            source: source || 'Nicht verfügbar'
          });
        }
      });
    }

    // 3. Extract Asphaltgranulat M.-%
    if (data?.full_data?.asphaltgranulat) {
      if (Array.isArray(data.full_data.asphaltgranulat)) {
        const asphaltSum = data.full_data.asphaltgranulat.reduce((sum, item) => {
          const percentage = item.massPercentage;
          return sum + (percentage !== undefined && percentage !== null ? percentage : 0);
        }, 0);

        if (asphaltSum > 0) {
          result.asphalt = Math.round(asphaltSum * 10) / 10;
        }
      } else if (data.full_data.asphaltgranulat.massPercentage !== undefined &&
                 data.full_data.asphaltgranulat.massPercentage !== null) {
        result.asphalt = Math.round(data.full_data.asphaltgranulat.massPercentage * 10) / 10;
      }
    }

    // 4. Extract Frischbindemittelzugabe M.-% from Binder Content
    if (data?.binder_content?.freshBinder !== undefined &&
        data.binder_content.freshBinder !== null) {
      result.bindemittel = Math.round(data.binder_content.freshBinder * 10) / 10;
    }

    // 5. Extract and map Fresh Binder Type
    if (data?.binder_content?.freshBinderType) {
      const mappedType = mapBinderType(data.binder_content.freshBinderType);
      if (mappedType) {
        result.selectedBindemittel = mappedType;
      }
    }

    // 6. Extract Distance Values
    if (data?.full_data?.aggregateComposition && data?.material_info?.productionLocation) {
      // Extract Füller distance
      const fuellerRow = data.full_data.aggregateComposition.find(item => {
        const lieferkornung = item.lieferkornung?.toLowerCase() || '';
        const bezeichnung = item.bezeichnung?.toLowerCase() || '';
        return lieferkornung.includes('füller') || bezeichnung.includes('füller');
      });

      if (fuellerRow) {
        const fuellerSource = fuellerRow.bezugsquelle || fuellerRow.gewinnungsstatte;
        if (fuellerSource && fuellerSource !== 'Nicht verfügbar') {
          const fuellerDistance = getDistance(fuellerSource);
          if (fuellerDistance !== null && fuellerDistance !== undefined) {
            result.fuellerDistance = fuellerDistance;
          }
        }
      }
    }

    // 7. Extract Asphaltgranulat Distance (Construction Site to Production Location)
    if (constructionSite?.address && data?.material_info?.productionLocation) {
      const asphaltDistance = getConstructionSiteDistance();
      if (asphaltDistance !== null && asphaltDistance !== undefined) {
        result.asphaltDistance = asphaltDistance;
      }
    }

    // 8. Extract Bindemittel Distance (Hersteller → Lieferwerk)
    if (typeof binderHersteller?.lat === 'number' &&
        typeof binderHersteller?.lng === 'number' &&
        data?.material_info?.productionLocation) {
      const bindemittelDistance = getBinderHerstellerDistance();
      if (bindemittelDistance !== null && bindemittelDistance !== undefined) {
        result.bindemittelDistance = bindemittelDistance;
      }
    }

    return result;
  };

  const handleTransferValues = async () => {
    const extractedValues = extractAllValues();

    // Filter out null/empty values to only transfer available data
    const valuesToTransfer = {};
    Object.entries(extractedValues).forEach(([key, value]) => {
      if (key === 'gesteinComponents') {
        // Always include gesteinComponents array (even if empty)
        if (value && value.length > 0) {
          valuesToTransfer[key] = value;
        }
      } else if (value !== null) {
        valuesToTransfer[key] = value;
      }
    });

    if (Object.keys(valuesToTransfer).length > 0 && onTransferValues) {
      onTransferValues(valuesToTransfer);
    }

    // Persist Raumdichte as dichte (t/m³) for matching material in constructionLayerAreas.
    // Raumdichte (apparent density of the compacted Marshall specimen) is the correct
    // density for computing built-in tonnage. 1 g/cm³ = 1 t/m³ numerically, so the
    // value transfers directly.
    const raumdichteValue = typeof data?.raumdichte === 'number' ? data.raumdichte : null;
    if (raumdichteValue && materialName && projectName) {
      setIsApplyingDichte(true);
      setDichteApplyError(null);
      try {
        const result = await simulationService.updateMaterialDichte(projectName, materialName, raumdichteValue);
        if (result.success && onMaterialDichteApplied) {
          onMaterialDichteApplied();
        }
      } catch (err) {
        console.error('Failed to apply Raumdichte to layer areas:', err);
        setDichteApplyError(err.message || 'Aktualisierung der Dichte fehlgeschlagen');
        setIsApplyingDichte(false);
        return; // keep modal open so user sees the error
      }
      setIsApplyingDichte(false);
    }

    onClose();
  };

  const canTransferValues = () => {
    const extractedValues = extractAllValues();
    return extractedValues.fueller !== null ||
           extractedValues.gesteinComponents.length > 0 ||
           extractedValues.asphalt !== null ||
           extractedValues.bindemittel !== null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-green-500 mr-3" />
            <h2 className="text-xl font-semibold">Zusammensetzung Asphaltmischgut</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
              <span className="ml-3 text-gray-600">Dokument wird verarbeitet...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="text-red-600 font-medium">Upload-Fehler</div>
              </div>
              <div className="text-red-700 mt-2">{error}</div>
            </div>
          )}

          {data && !isLoading && !error && (
            <div className="space-y-6">
              {/* Document Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-gray-600" />
                  Dokumentinformationen
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center">
                    <Hash className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-sm text-gray-600">Dokumentnummer:</span>
                    <span className="ml-2 font-medium">
                      {data.document_info?.documentNumber || 'Nicht verfügbar'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-sm text-gray-600">Datum:</span>
                    <span className="ml-2 font-medium">
                      {formatDate(data.document_info?.date)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Package className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-sm text-gray-600">Typ:</span>
                    <span className="ml-2 font-medium">
                      {data.document_info?.documentType || 'Materialspezifikation'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Hash className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-sm text-gray-600">Artikelnummer:</span>
                    <span className="ml-2 font-medium">
                      {data.material_info?.articleNumber || data.material_info?.materialType || 'Nicht verfügbar'}
                    </span>
                  </div>
                </div>

                {/* Production Location and Construction Site */}
                {(data.material_info?.productionLocation || constructionSite) && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Production Location */}
                      {data.material_info?.productionLocation && (
                        <div className="flex items-start">
                          <Building2 className="h-4 w-4 mr-2 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <span className="text-sm text-gray-600">Lieferwerk:</span>
                            <div className="mt-1 font-medium text-gray-900 leading-relaxed flex items-center">
                              <span className="flex-1">
                                {data.material_info.productionLocation}
                              </span>
                              <button
                                onClick={handleProductionPlantClick}
                                disabled={!googleMapsLoaded}
                                className="ml-2 text-teal-600 hover:text-teal-800 cursor-pointer hover:bg-teal-50 rounded p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Lieferwerk Standort anzeigen"
                              >
                                <MapPin className="h-4 w-4" />
                              </button>
                            </div>
                            {productionPlantLocation ? (
                              <div className="text-xs text-gray-500 mt-1">
                                {productionPlantLocation.lat?.toFixed(6)}, {productionPlantLocation.lng?.toFixed(6)}
                              </div>
                            ) : plantGeocodeLoading ? (
                              <div className="text-xs text-gray-400 mt-1 italic">Standort wird ermittelt…</div>
                            ) : googleMapsLoaded ? (
                              <div className="text-xs text-gray-400 mt-1 italic">Standort nicht gefunden</div>
                            ) : null}
                          </div>
                        </div>
                      )}

                      {/* Construction Site */}
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 mr-2 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="text-sm text-gray-600">Baustelle:</span>
                          <div className="mt-1 font-medium text-gray-900 leading-relaxed flex items-center">
                            <span className="flex-1">
                              {constructionSite ? constructionSite.address : 'Nicht verfügbar'}
                            </span>
                            <button
                              onClick={handleConstructionSiteClick}
                              className="ml-2 text-orange-600 hover:text-orange-800 cursor-pointer hover:bg-orange-50 rounded p-1 transition-colors"
                              title="Baustelle auswählen"
                            >
                              <MapPin className="h-4 w-4" />
                            </button>
                          </div>
                          {constructionSite && (
                            <div className="text-xs text-gray-500 mt-1">
                              {constructionSite.lat?.toFixed(6)}, {constructionSite.lng?.toFixed(6)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Asphaltmischguteigenschaften */}
              {(() => {
                const hasValue = (v) => typeof v === 'number' && isFinite(v);
                const formatDensity = (v) => `${v.toLocaleString('de-DE')} g/cm³`;

                const allCards = [
                  { key: 'rohdichte', label: 'Rohdichte (Asphaltmischgut)', value: data.rohdichte },
                  { key: 'rohdichteGK', label: 'Rohdichte (Gesteinskörnung)', value: data.rohdichteGesteinskornung },
                  { key: 'raumdichte', label: 'Raumdichte (Marshall-Prüfkörper)', value: data.raumdichte },
                ];
                const cards = allCards.filter(c => hasValue(c.value));

                if (cards.length === 0) return null;

                const gridCols = cards.length === 3 ? 'md:grid-cols-3'
                  : cards.length === 2 ? 'md:grid-cols-2'
                  : 'md:grid-cols-1';

                return (
                  <div className="bg-teal-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Package className="h-5 w-5 mr-2 text-teal-600" />
                      Asphaltmischguteigenschaften
                    </h3>
                    <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
                      {cards.map((card) => (
                        <div key={card.key} className="text-center p-3 bg-white rounded-lg border">
                          <div className="text-2xl font-bold text-teal-600">
                            {formatDensity(card.value)}
                          </div>
                          <div className="text-sm text-gray-600">{card.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Binder Content */}
              {data.binder_content && (() => {
                const bc = data.binder_content;
                const hasRecycled = bc.recycledBinder !== undefined && bc.recycledBinder !== null;
                const hasZusatze = bc.zusatzeBinder !== undefined && bc.zusatzeBinder !== null;
                const cards = [];

                cards.push({
                  key: 'fresh',
                  label: 'Frischbindemittel',
                  value: bc.freshBinder,
                  type: bc.freshBinderType,
                  color: 'text-green-600',
                });

                if (hasRecycled) {
                  cards.push({
                    key: 'recycled',
                    label: bc.recycledBinderLabel || 'Bindemittel aus RA',
                    value: bc.recycledBinder,
                    type: bc.recycledBinderType,
                    color: 'text-blue-600',
                  });
                }

                if (hasZusatze) {
                  cards.push({
                    key: 'zusatze',
                    label: 'Bindemittel aus Zusätzen',
                    value: bc.zusatzeBinder,
                    type: bc.zusatzeBinderType,
                    color: 'text-orange-600',
                  });
                }

                cards.push({
                  key: 'total',
                  label: 'Gesamtbindemittel',
                  value: bc.totalBinder,
                  type: null,
                  color: 'text-purple-600',
                });

                const gridCols = cards.length === 4 ? 'md:grid-cols-4' : cards.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';

                return (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Percent className="h-5 w-5 mr-2 text-green-600" />
                      Bindemittelgehalt
                    </h3>
                    <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
                      {cards.map((card) => (
                        <div key={card.key} className="text-center p-3 bg-white rounded-lg border">
                          <div className={`text-2xl font-bold ${card.color}`}>
                            {formatPercentage(card.value)}
                          </div>
                          <div className="text-sm text-gray-600">{card.label}</div>
                          {card.type && (
                            <div className="text-xs text-gray-400 mt-1 font-medium">
                              {card.type}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Single Hersteller (manufacturer) entry shared by the binder section */}
                    <div className="mt-4 pt-3 border-t border-green-200 bg-white rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">Hersteller</div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={binderHersteller.name || ''}
                          onChange={(e) => handleBinderHerstellerNameChange(e.target.value)}
                          placeholder="Hersteller eingeben…"
                          className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <button
                          type="button"
                          onClick={handleBinderHerstellerClick}
                          disabled={!googleMapsLoaded}
                          className="text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                          title="Hersteller-Standort auswählen"
                        >
                          <MapPin className="h-4 w-4" />
                        </button>
                      </div>
                      {typeof binderHersteller.lat === 'number' && typeof binderHersteller.lng === 'number' && (
                        <div className="text-xs text-gray-500 mt-2">
                          {binderHersteller.address && (
                            <span className="block truncate">{binderHersteller.address}</span>
                          )}
                          <span>{binderHersteller.lat.toFixed(6)}, {binderHersteller.lng.toFixed(6)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Aggregate Composition */}
              {data.full_data?.aggregateComposition && data.full_data.aggregateComposition.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-blue-600" />
                    Gesteinskörnungsgemisch
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Nr.</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">M.-%</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezeichnung</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Lieferkörnung</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezugsquelle</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Entfernung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.full_data.aggregateComposition.map((item, index) => {
                          const sourceLocation = item.bezugsquelle || item.gewinnungsstatte || 'Nicht verfügbar';
                          const distance = getDistance(sourceLocation);

                          return (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2 font-medium">{item.nr}</td>
                              <td className="px-4 py-2 text-green-600 font-semibold">
                                {formatPercentage(item.massPercentage)}
                              </td>
                              <td className="px-4 py-2">{item.bezeichnung}</td>
                              <td className="px-4 py-2">{item.lieferkornung}</td>
                              <td className="px-4 py-2">
                                <div className="flex items-center">
                                  <MapPin className="h-3 w-3 mr-1 text-gray-500 flex-shrink-0" />
                                  <span className="truncate">{sourceLocation}</span>
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center">
                                  <button
                                    onClick={() => handleRouteClick(sourceLocation)}
                                    disabled={!sourceLocation || sourceLocation === 'Nicht verfügbar' || !data?.material_info?.productionLocation}
                                    className={`mr-1 flex-shrink-0 transition-colors ${
                                      sourceLocation && sourceLocation !== 'Nicht verfügbar' && data?.material_info?.productionLocation
                                        ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:bg-blue-50 rounded p-0.5'
                                        : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                    title={
                                      sourceLocation && sourceLocation !== 'Nicht verfügbar' && data?.material_info?.productionLocation
                                        ? 'Klicken um Route anzuzeigen'
                                        : 'Route nicht verfügbar'
                                    }
                                  >
                                    <Route className="h-3 w-3" />
                                  </button>
                                  <span className="text-sm">{formatDistance(distance)}</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Asphaltgranulat */}
              {data.full_data?.asphaltgranulat && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-purple-600" />
                    Asphaltgranulat
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Nr.</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">M.-%</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezeichnung</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezugsquelle</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Entfernung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(data.full_data.asphaltgranulat) ? (
                          data.full_data.asphaltgranulat.map((item, index) => {
                            const constructionSiteDistance = getConstructionSiteDistance();
                            return (
                              <tr key={index} className="border-t">
                                <td className="px-4 py-2 font-medium">{item.nr || 'G1'}</td>
                                <td className="px-4 py-2 text-purple-600 font-semibold">
                                  {formatPercentage(item.massPercentage)}
                                </td>
                                <td className="px-4 py-2">{item.bezeichnung || 'Asphaltgranulat'}</td>
                                <td className="px-4 py-2">
                                  <span className="text-sm">
                                    {constructionSite ? constructionSite.address : 'Nicht verfügbar'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center">
                                    <button
                                      onClick={() => handleRouteClick(constructionSite?.address)}
                                      disabled={!constructionSite?.address || !data?.material_info?.productionLocation}
                                      className={`mr-1 flex-shrink-0 transition-colors ${
                                        constructionSite?.address && data?.material_info?.productionLocation
                                          ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:bg-blue-50 rounded p-0.5'
                                          : 'text-gray-400 cursor-not-allowed'
                                      }`}
                                      title={
                                        constructionSite?.address && data?.material_info?.productionLocation
                                          ? 'Klicken um Route anzuzeigen'
                                          : 'Route nicht verfügbar'
                                      }
                                    >
                                      <Route className="h-3 w-3" />
                                    </button>
                                    <span className="text-sm">{formatDistance(constructionSiteDistance)}</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          (() => {
                            const constructionSiteDistance = getConstructionSiteDistance();
                            return (
                              <tr className="border-t">
                                <td className="px-4 py-2 font-medium">G1</td>
                                <td className="px-4 py-2 text-purple-600 font-semibold">
                                  {formatPercentage(data.full_data.asphaltgranulat.massPercentage)}
                                </td>
                                <td className="px-4 py-2">Asphaltgranulat</td>
                                <td className="px-4 py-2">
                                  <span className="text-sm">
                                    {constructionSite ? constructionSite.address : 'Nicht verfügbar'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center">
                                    <button
                                      onClick={() => handleRouteClick(constructionSite?.address)}
                                      disabled={!constructionSite?.address || !data?.material_info?.productionLocation}
                                      className={`mr-1 flex-shrink-0 transition-colors ${
                                        constructionSite?.address && data?.material_info?.productionLocation
                                          ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:bg-blue-50 rounded p-0.5'
                                          : 'text-gray-400 cursor-not-allowed'
                                      }`}
                                      title={
                                        constructionSite?.address && data?.material_info?.productionLocation
                                          ? 'Klicken um Route anzuzeigen'
                                          : 'Route nicht verfügbar'
                                      }
                                    >
                                      <Route className="h-3 w-3" />
                                    </button>
                                    <span className="text-sm">{formatDistance(constructionSiteDistance)}</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })()
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Bindemittel: binder shipped from Hersteller → Lieferwerk */}
              {data.binder_content?.freshBinderType && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-green-600" />
                    Bindemittel
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezeichnung</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezugsquelle</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Entfernung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const hasHerstellerCoords = typeof binderHersteller?.lat === 'number' && typeof binderHersteller?.lng === 'number';
                          const productionLabel = data?.material_info?.productionLocation;
                          const routeReady = hasHerstellerCoords && !!productionLabel;
                          return (
                            <tr className="border-t">
                              <td className="px-4 py-2 text-green-700 font-semibold">
                                {data.binder_content.freshBinderType}
                              </td>
                              <td className="px-4 py-2">
                                {binderHersteller.name && (
                                  <div className="text-sm text-gray-900">{binderHersteller.name}</div>
                                )}
                                {binderHersteller.address && (
                                  <div className="text-sm text-gray-700">{binderHersteller.address}</div>
                                )}
                                {hasHerstellerCoords && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {binderHersteller.lat.toFixed(6)}, {binderHersteller.lng.toFixed(6)}
                                  </div>
                                )}
                                {!binderHersteller.name && !binderHersteller.address && !hasHerstellerCoords && (
                                  <span className="text-sm text-gray-400">Nicht verfügbar</span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center">
                                  <button
                                    onClick={handleBindemittelRouteClick}
                                    disabled={!routeReady}
                                    className={`mr-1 flex-shrink-0 transition-colors ${
                                      routeReady
                                        ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:bg-blue-50 rounded p-0.5'
                                        : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                    title={
                                      routeReady
                                        ? 'Klicken um Route Hersteller → Lieferwerk anzuzeigen'
                                        : 'Route nicht verfügbar — Hersteller-Standort unten festlegen'
                                    }
                                  >
                                    <Route className="h-3 w-3" />
                                  </button>
                                  <span className="text-sm">{formatDistance(getBinderHerstellerDistance())}</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Asphaltmischgut: the finished mix shipped from Lieferwerk → Baustelle */}
              {data.material_info?.articleNumber && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-amber-600" />
                    Asphaltmischgut
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezeichnung</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Bezugsquelle</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Entfernung</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="px-4 py-2 text-amber-700 font-semibold">
                            {data.material_info.articleNumber}
                          </td>
                          <td className="px-4 py-2">
                            <div className="text-sm">
                              {data.material_info?.productionLocation || 'Nicht verfügbar'}
                            </div>
                            {productionPlantLocation && (
                              <div className="text-xs text-gray-500 mt-1">
                                {productionPlantLocation.lat?.toFixed(6)}, {productionPlantLocation.lng?.toFixed(6)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center">
                              <button
                                onClick={handleAsphaltmischgutRouteClick}
                                disabled={!data?.material_info?.productionLocation || !constructionSite?.address}
                                className={`mr-1 flex-shrink-0 transition-colors ${
                                  data?.material_info?.productionLocation && constructionSite?.address
                                    ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:bg-blue-50 rounded p-0.5'
                                    : 'text-gray-400 cursor-not-allowed'
                                }`}
                                title={
                                  data?.material_info?.productionLocation && constructionSite?.address
                                    ? 'Klicken um Route Lieferwerk → Baustelle anzuzeigen'
                                    : 'Route nicht verfügbar — Baustelle auswählen'
                                }
                              >
                                <Route className="h-3 w-3" />
                              </button>
                              <span className="text-sm">{formatDistance(getConstructionSiteDistance())}</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Available Values Summary */}
              {canTransferValues() && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-gray-600" />
                    Für die Berechnung übernehmen
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Material</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Anteil</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Entfernung</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Zusätzliche Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const values = extractAllValues();
                          const materialRows = [];

                          // Füller row
                          if (values.fueller !== null) {
                            materialRows.push({
                              material: 'Füller',
                              percentage: values.fueller,
                              distance: values.fuellerDistance,
                              info: null,
                              color: 'text-blue-600'
                            });
                          }

                          // Individual Gesteinskörnungen rows (one per M-component)
                          values.gesteinComponents.forEach(comp => {
                            materialRows.push({
                              material: `${comp.nr} ${comp.bezeichnung}`,
                              percentage: comp.percentage,
                              distance: comp.distance,
                              info: comp.lieferkornung !== 'N/A' ? comp.lieferkornung : null,
                              color: 'text-blue-600'
                            });
                          });

                          // Asphaltgranulat row
                          if (values.asphalt !== null) {
                            materialRows.push({
                              material: 'Asphaltgranulat',
                              percentage: values.asphalt,
                              distance: values.asphaltDistance,
                              info: null,
                              color: 'text-purple-600'
                            });
                          }

                          // Frischbindemittel row
                          if (values.bindemittel !== null) {
                            materialRows.push({
                              material: 'Frischbindemittel',
                              percentage: values.bindemittel,
                              distance: values.bindemittelDistance,
                              info: values.selectedBindemittel,
                              color: 'text-green-600'
                            });
                          }

                          return materialRows.map((row, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2 font-medium">{row.material}</td>
                              <td className={`px-4 py-2 font-semibold ${row.color}`}>
                                {row.percentage}%
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {row.distance ? `${row.distance} km` : '-'}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {row.info || '-'}
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
          {data && !isLoading && !error && canTransferValues() && (
            <div className="flex justify-end items-center gap-3">
              {dichteApplyError && (
                <span className="text-sm text-red-600">{dichteApplyError}</span>
              )}
              <button
                onClick={handleTransferValues}
                disabled={isApplyingDichte}
                className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isApplyingDichte ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    Wird übernommen...
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-3 w-3 mr-1.5" />
                    Werte übernehmen
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Route Modal */}
      <RouteModal
        isOpen={routeModalOpen}
        onClose={handleRouteModalClose}
        origin={selectedRoute.origin}
        destination={selectedRoute.destination}
        originLocation={selectedRoute.originLocation}
        destinationLocation={selectedRoute.destinationLocation}
        waypoints={routeData[selectedRoute.originalCacheKey]?.waypoints || []}
        googleMapsLoaded={googleMapsLoaded}
        onSave={handleRouteSave}
      />

      {/* Construction Site Modal */}
      <ConstructionSiteModal
        isOpen={constructionSiteModalOpen}
        onClose={handleConstructionSiteModalClose}
        onSave={handleConstructionSiteSave}
        googleMapsLoaded={googleMapsLoaded}
        initialLocation={constructionSite}
        productionLocation={data?.material_info?.productionLocation}
      />

      {/* Production Plant (Lieferwerk) Location Modal */}
      <ProductionLocationModal
        isOpen={productionPlantModalOpen}
        onClose={handleProductionPlantModalClose}
        onSave={handleProductionPlantSave}
        googleMapsLoaded={googleMapsLoaded}
        initialLocation={productionPlantLocation}
        productionName={data?.material_info?.productionLocation}
      />

      {/* Binder Hersteller Location Modal */}
      <ProductionLocationModal
        isOpen={binderHerstellerModalOpen}
        onClose={handleBinderHerstellerModalClose}
        onSave={handleBinderHerstellerSave}
        googleMapsLoaded={googleMapsLoaded}
        initialLocation={binderHersteller}
        productionName={binderHersteller.name || null}
        subjectLabel="Hersteller Standort"
        searchHintPrefix=""
      />
    </div>
  );
};

export default MaterialSpecificationModal;
